import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-vh-100 py-5" style={{ backgroundColor: '#f8f9fa' }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={8} md={10}>
            <div className="mb-4">
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>
              <h1 className="fw-bold text-dark mb-4">Privacy Policy</h1>
              <p className="text-muted">Last Updated: March 1, 2026</p>
            </div>

            <div className="bg-white p-4 rounded shadow-sm">
              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">1. Introduction</h2>
                <p className="text-muted">
                  NetworqPay ("we", "us", "our", or "Company") operates the NetworqPay website and platform
                  (the "Service"). This page informs you of our policies regarding the collection, use, and
                  disclosure of personal data when you use our Service and the choices you have associated with
                  that data.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">2. Information Collection and Use</h2>
                <p className="text-muted">
                  We collect several different types of information for various purposes to provide and improve
                  our Service to you.
                </p>

                <h5 className="fw-semibold text-dark mt-4 mb-2">Types of Data Collected:</h5>
                <ul className="text-muted">
                  <li>
                    <strong>Personal Data:</strong> While using our Service, we may ask you to provide us with
                    certain personally identifiable information that can be used to contact or identify you
                    ("Personal Data"). This may include, but is not limited to:
                    <ul className="mt-2">
                      <li>Email address</li>
                      <li>First name and last name</li>
                      <li>Phone number</li>
                      <li>Address, State, Province, ZIP/Postal code, City</li>
                      <li>Cookies and Usage Data</li>
                      <li>Bank account details for withdrawal purposes</li>
                      <li>KYC documents (PAN, Aadhaar, Address proof)</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Usage Data:</strong> We may also collect information about how the Service is accessed
                    and used ("Usage Data"). This may include information such as your computer's Internet Protocol
                    address (e.g. IP address), browser type, pages of our Service that you visit, the time and date
                    of your visit, and other diagnostic data.
                  </li>
                  <li>
                    <strong>Cookies and Tracking Data:</strong> We use cookies and similar tracking technologies to
                    track activity on our Service and hold certain information.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">3. Use of Data</h2>
                <p className="text-muted">
                  NetworqPay uses the collected data for various purposes:
                </p>
                <ul className="text-muted">
                  <li>To provide and maintain our Service</li>
                  <li>To notify you about changes to our Service</li>
                  <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information so that we can improve our Service</li>
                  <li>To monitor the usage of our Service</li>
                  <li>To detect, prevent and address technical issues and fraudulent activity</li>
                  <li>To verify user identity and prevent fraud</li>
                  <li>To process withdrawal requests and payments</li>
                  <li>To send marketing and promotional communications</li>
                </ul>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">4. Security of Data</h2>
                <p className="text-muted">
                  The security of your data is important to us but remember that no method of transmission over
                  the Internet or method of electronic storage is 100% secure. While we strive to use commercially
                  acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
                </p>
                <p className="text-muted mt-3">
                  We implement appropriate technical and organizational measures to protect your personal data,
                  including encryption, secure authentication, and restricted access controls. However, you are
                  responsible for keeping your account password confidential and secure.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">5. Service Providers</h2>
                <p className="text-muted">
                  We may employ third party companies and individuals to facilitate our Service ("Service Providers"),
                  to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing
                  how our Service is used. These third parties have access to your Personal Data only to perform these
                  tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">6. Links to Other Sites</h2>
                <p className="text-muted">
                  Our Service may contain links to other sites that are not operated by us. This Privacy Policy does
                  not apply to these external sites, and we are not responsible for their privacy practices. We strongly
                  advise you to review the Privacy Policy of every site you visit.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">7. Children's Privacy</h2>
                <p className="text-muted">
                  Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect
                  personally identifiable information from anyone under 18. If you are a parent or guardian and you are
                  aware that your child has provided us with Personal Data, please contact us.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">8. Changes to This Privacy Policy</h2>
                <p className="text-muted">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the
                  new Privacy Policy on this page and updating the "Last Updated" date at the top of this page. You are
                  advised to review this Privacy Policy periodically for any changes.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">9. Your Data Rights</h2>
                <p className="text-muted">
                  You have certain rights regarding your personal data, including:
                </p>
                <ul className="text-muted">
                  <li>The right to access your personal data</li>
                  <li>The right to correct inaccurate or incomplete data</li>
                  <li>The right to request deletion of your data</li>
                  <li>The right to restrict the processing of your data</li>
                  <li>The right to data portability</li>
                  <li>The right to object to processing</li>
                  <li>The right to withdraw consent at any time</li>
                </ul>
                <p className="text-muted mt-3">
                  To exercise any of these rights, please contact us at the email address provided below.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">10. Data Retention</h2>
                <p className="text-muted">
                  We will retain your Personal Data for as long as necessary to provide our Service and fulfill the
                  purposes outlined in this Privacy Policy. You can request deletion of your account and associated
                  data at any time, subject to legal and regulatory requirements.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">11. International Data Transfer</h2>
                <p className="text-muted">
                  Your information may be transferred to, and maintained on, computers located outside of your state,
                  province, country or other governmental jurisdiction where the data protection laws may differ from
                  those of your jurisdiction. By consenting to this Privacy Policy, you consent to any transfer of your
                  information outside of your country.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">12. Do Not Track Signals</h2>
                <p className="text-muted">
                  Some browsers include a Do Not Track feature. These features are typically turned off by default.
                  Most web services, including NetworqPay, do not currently respond to Do Not Track browser signals.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">13. Cookie Policy</h2>
                <p className="text-muted">
                  We use cookies to enhance your experience on our Service. Cookies are small data files stored on your
                  device that help us remember your preferences and track your usage. You can choose to disable cookies
                  through your browser settings, but this may affect the functionality of our Service.
                </p>
              </section>

              <section className="mb-5">
                <h2 className="h4 fw-bold text-dark mb-3">14. Third-Party Analytics</h2>
                <p className="text-muted">
                  NetworqPay may use third-party analytics services to analyze the use of our Service. These services
                  may collect information about your online activities and the devices you use. The data collected will
                  be used in accordance with their own privacy policies.
                </p>
              </section>

              <section>
                <h2 className="h4 fw-bold text-dark mb-3">15. Contact Us</h2>
                <p className="text-muted">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="bg-light p-3 rounded mt-3">
                  <p className="mb-1"><strong>Email:</strong> privacy@networqpay.com</p>
                  <p className="mb-1"><strong>Address:</strong> NetworqPay, India</p>
                  <p className="mb-1"><strong>Phone:</strong> +91-XXXXXXXXXX</p>
                  <p className="mb-0"><strong>Support Hours:</strong> Monday - Friday, 9 AM - 6 PM IST</p>
                </div>
              </section>

              <div className="mt-5 pt-4 border-top">
                <p className="text-muted small">
                  By using NetworqPay, you acknowledge that you have read and understood this Privacy Policy and agree
                  to the collection and use of your information as described herein.
                </p>
                <Button
                  onClick={() => navigate('/register')}
                  className="fw-semibold py-2"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  <i className="fas fa-checkmark me-2"></i>
                  I Understand - Continue to Sign Up
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
